import { useMemo, useState } from "react";
import { Banknote, Info, Package, Minus, Plus, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import { refundsViaChannel } from "../helpers/channelRefunds";
import type { DispatchOrder, DispatchOrderItem } from "../../types/Dispatch";
import { displayItemCode } from "./item-code";

// Quick Refund from the flagged list — resolves the flag without opening
// the pack screen. Prefilled with what the flag says is outstanding:
//
//   Auto-flagged (order shipped short, packed_at set): the outstanding
//   units per line — missing + damaged + wrong — capped there, because
//   the shipped units already went to the customer.
//   User-flagged (flagged before packing): nothing shipped, so every
//   line at its full quantity — the "refund the whole order" case.
//
// Counts are editable; a count stepped to 0 refunds nothing on that line.
// All counts at 0 is allowed and called out — it resolves the flag with
// the order marked Fulfilled, exactly as the full resolve page permits.
//
// The shipping-refund field mirrors the full page: it feeds the totals
// and the "refund issued" toast, and (like the full page) is NOT sent to
// the BE — the money side is handled off-Portal.

export type RefundLine = { line_position: number | undefined; refund_count: number };

type Props = {
    order: DispatchOrder;
    busy: boolean;
    onCancel: () => void;
    onConfirm: (refundLines: RefundLine[], totalRefund: number) => void;
};

const formatMoney = (value: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
};

const outstandingOf = (i: DispatchOrderItem) =>
    (i.missingCount || 0) + (i.damagedCount || 0) + (i.wrongItemCount || 0);

const FlaggedRefundModal = ({ order, busy, onCancel, onConfirm }: Props) => {
    const { t } = useTranslation();
    const currency = order.currency || "GBP";
    const isUserFlagged = !order.packed_at;

    // The refundable lines with their prefill + cap. Auto-flagged orders
    // only surface lines with something outstanding; user-flagged orders
    // surface every line at full quantity.
    const lines = useMemo(() => {
        const out: { item: DispatchOrderItem; cap: number }[] = [];
        for (const item of order.items) {
            const cap = isUserFlagged ? item.quantity : outstandingOf(item);
            if (cap > 0) out.push({ item, cap });
        }
        return out;
    }, [order, isUserFlagged]);

    const [counts, setCounts] = useState<Record<string, number>>(
        () => Object.fromEntries(lines.map(l => [l.item.item_id, l.cap])),
    );
    const [shippingRefund, setShippingRefund] = useState("");

    const countOf = (id: string) => counts[id] ?? 0;
    const step = (id: string, cap: number, delta: number) =>
        setCounts(prev => ({ ...prev, [id]: Math.min(cap, Math.max(0, (prev[id] ?? 0) + delta)) }));

    const refundUnits = lines.reduce((s, l) => s + countOf(l.item.item_id), 0);
    const itemsRefundTotal = lines.reduce(
        (s, l) => s + countOf(l.item.item_id) * (l.item.unit_price || 0), 0);
    const shippingRefundValue = (() => {
        const n = parseFloat(shippingRefund);
        if (!Number.isFinite(n) || n <= 0) return 0;
        return Math.min(n, order.shipping_paid || 0);
    })();
    const totalRefund = itemsRefundTotal + shippingRefundValue;

    // Mirror of the BE's status derivation so the leader sees what the
    // confirm will do before clicking it.
    const totalQuantity = order.items.reduce((s, i) => s + i.quantity, 0);
    const resultStatus = refundUnits === 0
        ? t("status_fulfilled")
        : refundUnits >= totalQuantity
            ? t("status_refunded")
            : t("status_partially_refunded");

    const confirm = () => {
        const refundLines: RefundLine[] = lines
            .filter(l => l.item.line_position !== undefined)
            .map(l => ({
                line_position: l.item.line_position,
                refund_count: countOf(l.item.item_id),
            }));
        onConfirm(refundLines, totalRefund);
    };

    return (
        <div className="modal-overlay" onClick={busy ? undefined : onCancel}>
            <div className="modal-card dispatch-flagged-action-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("quick_refund_title", { order: order.order_number })}</h2>
                    <button className="modal-close" onClick={onCancel} disabled={busy} aria-label={t("cancel")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <ul className="dispatch-replacement-lines">
                        {lines.map(({ item, cap }) => {
                            const n = countOf(item.item_id);
                            return (
                                <li key={item.item_id} className="dispatch-replacement-line">
                                    <span className="dispatch-replacement-thumb" aria-hidden="true">
                                        {item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt=""
                                                loading="lazy"
                                                onError={e => { e.currentTarget.style.display = "none"; }}
                                            />
                                        ) : (
                                            <Package size={20} />
                                        )}
                                    </span>
                                    <span className="dispatch-replacement-info">
                                        <span className="dispatch-replacement-title">{item.title || item.sku}</span>
                                        <span className="dispatch-replacement-sku">{displayItemCode(item.sku, item.barcode)}</span>
                                        <span className="dispatch-replacement-stock">
                                            {t("refund_line_outstanding", { n: String(cap) })}
                                            {" · "}
                                            {t("unit_price")}: {formatMoney(item.unit_price || 0, currency)}
                                        </span>
                                    </span>
                                    <span className="dispatch-replacement-stepper">
                                        <button
                                            type="button"
                                            onClick={() => step(item.item_id, cap, -1)}
                                            disabled={busy || n <= 0}
                                            aria-label="-1"
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <span className="dispatch-replacement-qty">{n}</span>
                                        <button
                                            type="button"
                                            onClick={() => step(item.item_id, cap, 1)}
                                            disabled={busy || n >= cap}
                                            aria-label="+1"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>

                    <label className="dispatch-refund-shipping-label">
                        <span className="dispatch-refund-shipping-row">
                            <span>{t("shipping_refund")}</span>
                            <span className="dispatch-refund-shipping-paid">
                                {typeof order.shipping_paid === "number" && order.shipping_paid > 0
                                    ? `${t("customer_paid_shipping")}: ${formatMoney(order.shipping_paid, currency)}`
                                    : t("no_shipping_charged")}
                            </span>
                        </span>
                        <input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={order.shipping_paid || undefined}
                            step="0.01"
                            value={shippingRefund}
                            onChange={e => setShippingRefund(e.target.value)}
                            placeholder="0.00"
                            disabled={busy || !order.shipping_paid}
                        />
                    </label>

                    <div className="dispatch-refund-totals">
                        <div className="dispatch-refund-total-row">
                            <span>{t("items_refund_total")}</span>
                            <span>{formatMoney(itemsRefundTotal, currency)}</span>
                        </div>
                        {shippingRefundValue > 0 && (
                            <div className="dispatch-refund-total-row">
                                <span>{t("shipping_refund")}</span>
                                <span>{formatMoney(shippingRefundValue, currency)}</span>
                            </div>
                        )}
                        <div className="dispatch-refund-total-row dispatch-refund-total-row--grand">
                            <span>{t("total_refund")}</span>
                            <span>{formatMoney(totalRefund, currency)}</span>
                        </div>
                    </div>

                    <p className={`dispatch-flagged-action-result${refundUnits === 0 ? " dispatch-flagged-action-result--warn" : ""}`}>
                        {refundUnits === 0
                            ? t("resolve_zero_refund_warning")
                            : t("resolve_status_result", { status: resultStatus })}
                    </p>

                    {totalRefund > 0 && !refundsViaChannel(order) && (
                        <p className="dispatch-refund-manual-note">
                            <Info size={14} /> {t("refund_manual_channel_note")}
                        </p>
                    )}

                    <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                        <button className="btn btn-ghost dispatch-modal-btn" onClick={onCancel} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button className="btn btn-success dispatch-modal-btn" onClick={confirm} disabled={busy}>
                            <Banknote size={14} />
                            {busy ? t("submitting") : t("process_refund")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlaggedRefundModal;
